class BrandDirectorySection extends HTMLElement {
  constructor() {
    super();
    this.onNavClick = this.onNavClick.bind(this);
    this.onSearchInput = this.onSearchInput.bind(this);
    this.onSearchClear = this.onSearchClear.bind(this);
    this.filterRaf = null;
    this.brandIndex = [];
    this.groupSections = new Map();
    this.navItems = new Map();
  }

  connectedCallback() {
    this.nav = this.querySelector('[data-brand-nav]');
    this.navWrapper = this.querySelector('[data-brand-nav-wrapper]');
    this.searchInput = this.querySelector('[data-brand-search]');
    this.clearButton = this.querySelector('[data-brand-clear]');
    this.noResults = this.querySelector('[data-brand-no-results]');

    if (!this.nav) return;

    this.buildSearchIndex();
    this.nav.addEventListener('click', this.onNavClick);

    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.onSearchInput);
      this.searchInput.addEventListener('search', this.onSearchInput);
    }

    if (this.clearButton) {
      this.clearButton.addEventListener('click', this.onSearchClear);
    }

    this.maybeScrollToHash();
  }

  disconnectedCallback() {
    this.nav?.removeEventListener('click', this.onNavClick);
    this.searchInput?.removeEventListener('input', this.onSearchInput);
    this.searchInput?.removeEventListener('search', this.onSearchInput);
    this.clearButton?.removeEventListener('click', this.onSearchClear);

    if (this.filterRaf) {
      cancelAnimationFrame(this.filterRaf);
      this.filterRaf = null;
    }
  }

  buildSearchIndex() {
    const items = this.querySelectorAll('[data-brand-item]');
    this.brandIndex = new Array(items.length);

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const name = item.getAttribute('data-brand-name') || '';
      const groupSection = item.closest('[data-brand-group]');
      const groupId = groupSection ? groupSection.getAttribute('data-brand-group') : '';

      this.brandIndex[i] = {
        item: item,
        normalizedName: name.toLowerCase(),
        groupId: groupId
      };

      if (groupId && groupSection && !this.groupSections.has(groupId)) {
        this.groupSections.set(groupId, groupSection);
      }
    }

    const navEntries = this.querySelectorAll('[data-brand-nav-item]');
    for (let i = 0; i < navEntries.length; i++) {
      const navItem = navEntries[i];
      const groupId = navItem.getAttribute('data-brand-group');
      if (groupId) {
        this.navItems.set(groupId, navItem);
      }
    }
  }

  onSearchInput() {
    if (this.filterRaf) return;

    this.filterRaf = requestAnimationFrame(() => {
      this.filterRaf = null;
      this.applySearchFilter(this.searchInput.value);
    });
  }

  onSearchClear() {
    this.searchInput.value = '';
    this.applySearchFilter('');
    this.searchInput.focus();
  }

  applySearchFilter(rawQuery) {
    const query = rawQuery.trim().toLowerCase();
    const isActive = query.length > 0;
    const visibleByGroup = new Map();
    let visibleCount = 0;
    const index = this.brandIndex;

    for (let i = 0; i < index.length; i++) {
      const entry = index[i];
      const matches = !isActive || entry.normalizedName.includes(query);
      entry.item.hidden = !matches;

      if (matches) {
        visibleCount++;
        const groupId = entry.groupId;
        visibleByGroup.set(groupId, (visibleByGroup.get(groupId) || 0) + 1);
      }
    }

    if (isActive) {
      this.groupSections.forEach((section, groupId) => {
        section.hidden = !visibleByGroup.has(groupId);
      });

      this.navItems.forEach((navItem, groupId) => {
        navItem.hidden = !visibleByGroup.has(groupId);
      });
    } else {
      this.groupSections.forEach((section) => {
        section.hidden = false;
      });

      this.navItems.forEach((navItem) => {
        navItem.hidden = false;
      });
    }

    if (this.noResults) {
      this.noResults.hidden = !isActive || visibleCount > 0;
    }

    if (this.navWrapper) {
      this.navWrapper.hidden = isActive && visibleCount === 0;
    }

    if (this.clearButton) {
      this.clearButton.hidden = !isActive;
    }
  }

  onNavClick(event) {
    const link = event.target.closest('[data-scroll-target]');
    if (!link || !this.nav.contains(link)) return;

    const hash = link.getAttribute('href');
    if (!hash || hash.charAt(0) !== '#') return;

    const targetId = hash.slice(1);
    const target = this.querySelector('#' + CSS.escape(targetId));
    if (!target) return;

    event.preventDefault();
    this.scrollToTarget(target, targetId);
  }

  scrollToTarget(target, targetId) {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start'
    });

    if (history.replaceState) {
      history.replaceState(null, '', '#' + targetId);
    } else {
      window.location.hash = targetId;
    }
  }

  maybeScrollToHash() {
    const hash = window.location.hash;
    if (!hash || hash.length < 2) return;

    const targetId = decodeURIComponent(hash.slice(1));
    const target = this.querySelector('#' + CSS.escape(targetId));
    if (!target) return;

    requestAnimationFrame(() => {
      this.scrollToTarget(target, targetId);
    });
  }
}

if (!customElements.get('brand-directory-section')) {
  customElements.define('brand-directory-section', BrandDirectorySection);
}
